import React from "react";
import { AlertTriangle, RotateCcw, ArrowLeft } from "lucide-react";

// Contains a render crash to ONE record instead of white-screening the whole page.
// It ALSO surfaces the exact failing field on-screen (error.message keeps the property
// name even in a minified prod build), so the root cause is readable without DevTools.
export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary] render crash:", error, info?.componentStack);
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;
    const label = this.props.label || "this page";
    return (
      <div data-testid="route-error-boundary" className="max-w-2xl mx-auto my-10 rounded-2xl border border-red-200 bg-white shadow-sm overflow-hidden">
        <div className="h-1 bg-red-500" aria-hidden />
        <div className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-red-50 text-red-600"><AlertTriangle className="w-5 h-5" /></span>
            <h2 className="font-display text-lg font-bold text-slate-900">Couldn't display {label}</h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            This record hit a display error. Nothing was changed — your other records are unaffected.
            Please screenshot this and send it over; the line below names exactly what failed.
          </p>
          <div data-testid="route-error-message" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 font-mono text-xs text-red-800 break-words mb-3">
            {error?.message || String(error)}
          </div>
          {info?.componentStack && (
            <details className="mb-4">
              <summary className="text-xs font-semibold text-slate-500 cursor-pointer">Technical details</summary>
              <pre data-testid="route-error-stack" className="mt-2 rounded-lg bg-slate-900 text-slate-200 text-[10px] leading-relaxed p-3 overflow-auto max-h-56 whitespace-pre-wrap">{info.componentStack}</pre>
            </details>
          )}
          <div className="flex gap-2">
            <button data-testid="route-error-back" onClick={() => window.history.back()} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <ArrowLeft className="w-4 h-4" />Go back
            </button>
            <button data-testid="route-error-reload" onClick={() => window.location.reload()} className="inline-flex items-center gap-1.5 rounded-lg bg-[#E0781A] hover:bg-[#C76714] px-3 py-2 text-sm font-medium text-white">
              <RotateCcw className="w-4 h-4" />Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
